import { formatInZone, isUnsociableHour, toUtcIso } from "@so/shared";
import { useMemo, useState } from "react";
import { useAvailability, useMe, useSetAvailability } from "../api/queries.js";
import {
  Alert,
  Badge,
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  cx,
} from "../components/ui.js";

const DURATIONS = [30, 45, 60] as const;

interface DayGroup {
  readonly key: string;
  readonly label: string;
  readonly slots: readonly { starts_at: string }[];
}

/** Groups windows by the specialist's own calendar day. */
function groupByDay(
  slots: readonly { starts_at: string }[],
  timezone: string,
): DayGroup[] {
  const groups = new Map<string, DayGroup>();

  for (const slot of slots) {
    const { date } = formatInZone(slot.starts_at, timezone);
    const existing = groups.get(date);
    groups.set(
      date,
      existing
        ? { ...existing, slots: [...existing.slots, slot] }
        : { key: date, label: date, slots: [slot] },
    );
  }

  return [...groups.values()];
}

/**
 * A specialist's own calendar.
 *
 * The API replaces the whole offered set rather than patching it, which is
 * what makes withdrawing a window possible. Windows that are already booked
 * are never removed - the handler keeps them and says so - because a patient
 * relying on a time should not lose it because their specialist tidied up
 * their calendar.
 */
export function Availability() {
  const { data: me } = useMe();
  const clinicianId = me?.userId ?? "";

  const { data, isPending, error, refetch } = useAvailability(clinicianId, true);
  const save = useSetAvailability(clinicianId);

  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [duration, setDuration] = useState<number>(45);
  const [notice, setNotice] = useState<string | undefined>();

  const viewerTimezone = me?.timezone ?? "UTC";

  const { open, booked } = useMemo(() => {
    const slots = data?.slots ?? [];
    return {
      open: slots.filter((s) => s.state === "available"),
      booked: slots.filter((s) => s.state === "booked"),
    };
  }, [data]);

  if (isPending) return <Loading label="Loading your calendar" />;
  if (error) return <ErrorNote error={error} />;

  /** Resubmits everything currently open, plus or minus one window. */
  async function submit(next: readonly { starts_at: string }[]) {
    const result = await save.mutateAsync(
      next.map((slot) => ({ starts_at: slot.starts_at, duration_minutes: duration })),
    );

    setNotice(
      result.kept_because_booked.length > 0
        ? `${result.kept_because_booked.length} window${result.kept_because_booked.length === 1 ? " was" : "s were"} kept because a patient has already booked ${result.kept_because_booked.length === 1 ? "it" : "them"}. Cancel the consultation if you need the time back.`
        : undefined,
    );

    await refetch();
  }

  async function add() {
    if (date === "" || time === "") return;

    // The input is in the specialist's own local time; everything is stored
    // in UTC. Building the instant through the browser's Date is only correct
    // because the specialist's browser is in their own timezone.
    const startsAt = toUtcIso(new Date(`${date}T${time}`));

    if (open.some((slot) => slot.starts_at === startsAt)) {
      setNotice("That window is already open.");
      return;
    }

    await submit([...open, { starts_at: startsAt }]);
    setDate("");
  }

  async function remove(startsAt: string) {
    await submit(open.filter((slot) => slot.starts_at !== startsAt));
  }

  return (
    <>
      <PageHeader eyebrow="Your calendar" title="Consultation windows">
        Times you are willing to take second-opinion consultations. Patients see only what is open
        here, in their own timezone.
      </PageHeader>

      <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
        <Card className="h-fit p-5">
          <h2 className="mb-4 text-[17px] font-semibold">Open a new window</h2>

          <div className="space-y-4">
            <Field label="Date">
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
              />
            </Field>

            <Field label="Start time" hint={`In your timezone (${viewerTimezone}).`}>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </Field>

            <Field label="Length">
              <Select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
                {DURATIONS.map((value) => (
                  <option key={value} value={value}>
                    {value} minutes
                  </option>
                ))}
              </Select>
            </Field>

            {save.error !== null && <ErrorNote error={save.error} />}

            <Button full loading={save.isPending} disabled={date === ""} onClick={() => void add()}>
              Open this window
            </Button>

            {notice !== undefined && <Alert tone="caution">{notice}</Alert>}
          </div>
        </Card>

        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-[17px] font-semibold">
              Open windows
              <span className="text-ink-faint ml-2 text-[14px] font-normal">{open.length}</span>
            </h2>

            {open.length === 0 ? (
              <Card className="text-ink-soft px-5 py-6 text-[14.5px]">
                No open windows. Patients cannot book you until you add some.
              </Card>
            ) : (
              /* Grouped by day rather than one flat list. Three weeks of
                 windows is sixty-odd rows, and a specialist looking for next
                 Tuesday should not have to scroll through all of them. */
              <div className="space-y-4">
                {groupByDay(open, viewerTimezone).map((day) => (
                  <div key={day.key}>
                    <h3 className="text-ink-soft mb-1.5 text-[13.5px] font-medium">{day.label}</h3>
                    <ul className="space-y-1.5">
                      {day.slots.map((slot) => {
                        const local = formatInZone(slot.starts_at, viewerTimezone);
                        const odd = isUnsociableHour(slot.starts_at, viewerTimezone);

                        return (
                          <Card
                            as="li"
                            key={slot.starts_at}
                            className={cx(
                              "flex items-center gap-3 px-4 py-2.5",
                              odd && "border-caution/40",
                            )}
                          >
                            <p className="text-ink flex-1 font-mono text-[14px]">
                              {local.time}
                              <span className="text-ink-faint ml-1.5 text-[12px]">
                                {local.zoneAbbreviation}
                              </span>
                            </p>
                            {odd && <Badge tone="caution">Outside hours</Badge>}
                            <Button variant="ghost" onClick={() => void remove(slot.starts_at)}>
                              Withdraw
                            </Button>
                          </Card>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>

          {booked.length > 0 && (
            <section>
              <h2 className="mb-3 text-[17px] font-semibold">
                Booked
                <span className="text-ink-faint ml-2 text-[14px] font-normal">{booked.length}</span>
              </h2>
              <ul className="space-y-2">
                {booked.map((slot) => {
                  const local = formatInZone(slot.starts_at, viewerTimezone);

                  return (
                    <Card as="li" key={slot.starts_at} className="flex items-center gap-4 px-4 py-3">
                      <div className="flex-1">
                        <p className="text-[14.5px] font-medium">{local.date}</p>
                        <p className="text-ink-soft mt-0.5 font-mono text-[13.5px]">
                          {local.time} {local.zoneAbbreviation}
                        </p>
                      </div>
                      <Badge tone="accent">Booked</Badge>
                    </Card>
                  );
                })}
              </ul>
              <p className="text-ink-faint mt-2.5 text-[12.5px] leading-relaxed">
                Booked windows cannot be withdrawn here. Open the consultation to cancel it if you
                genuinely need the time back — the patient will be told.
              </p>
            </section>
          )}
        </div>
      </div>
    </>
  );
}
